import { isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { environment } from "@env/environment";
import { EncryptedResponse } from "@shared/api-obfuscation";
import {
  ROUTES_DISCOVERY_UUID,
  ROUTES_DISCOVERY_RETRY_ATTEMPTS,
  ROUTES_DISCOVERY_RETRY_DELAY
} from "@shared/api-obfuscation/constants/api-obfuscation.constants";
import { RoutesDiscoveryResponse } from "@shared/api-obfuscation/models/route-mapping.interface";
import { firstValueFrom } from "rxjs";
import { retry } from "rxjs/operators";

import { EncryptionService } from "./encryption.service";

interface RouteInfo {
  uuid: string;
  isPattern: boolean;
  pattern?: string;
  params?: string[];
  regex?: RegExp;
}

@Injectable()
export class RouteMappingService {
  private routes: Array<{ pattern: RegExp; info: RouteInfo; originalPattern: string }> = [];
  private initialized = false;
  private urlCache: Map<string, string> = new Map();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private encryption: EncryptionService
  ) {}

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // In SSR environment, we'll skip the discovery call but still mark as initialized
    // The backend should recognize X-SSR-Request header and allow unobfuscated calls
    if (!isPlatformBrowser(this.platformId)) {
      console.warn("[ApiObfuscation] Running in SSR mode - routes will not be obfuscated");
      this.initialized = true;
      return;
    }

    try {
      const request$ = this.http.get<EncryptedResponse>(
        `${environment.classicApiUrl}/api/v2/o/${ROUTES_DISCOVERY_UUID}/`
      );

      // Only add retry in production environment
      const requestWithRetry$ = environment.production
        ? request$.pipe(
            retry({
              count: ROUTES_DISCOVERY_RETRY_ATTEMPTS - 1,
              delay: ROUTES_DISCOVERY_RETRY_DELAY
            })
          )
        : request$;

      const response = await firstValueFrom(requestWithRetry$);

      if (response.encrypted) {
        const decrypted = await this.encryption.decryptResponse(response);

        const discoveryResponse = decrypted as RoutesDiscoveryResponse;
        if (discoveryResponse.routes) {
          // Log routes in non-production mode
          if (!environment.production) {
            console.log("[ApiObfuscation] Discovered routes:");
            console.log("Static routes:");
            Object.entries(discoveryResponse.routes).forEach(([route, value]) => {
              if (typeof value === "string") {
                console.log(`  ${route} → ${value}`);
              }
            });
            console.log("Pattern routes:");
            Object.entries(discoveryResponse.routes).forEach(([route, value]) => {
              if (typeof value !== "string") {
                console.log(`  ${route} → ${value.uuid} (params: ${value.params.join(", ")})`);
              }
            });
          }

          Object.entries(discoveryResponse.routes).forEach(([route, value]) => {
            // Backend now sends JavaScript-compatible regex patterns
            try {
              const regexPattern = new RegExp(route);

              if (typeof value === "string") {
                // Static route (no params to extract)
                const info: RouteInfo = {
                  uuid: value,
                  isPattern: false,
                  pattern: route
                };
                this.routes.push({
                  pattern: regexPattern,
                  info,
                  originalPattern: route
                });
              } else {
                // Pattern-based route (has params to extract)
                const info: RouteInfo = {
                  uuid: value.uuid,
                  isPattern: true,
                  pattern: route,
                  params: value.params
                };
                this.routes.push({
                  pattern: regexPattern,
                  info,
                  originalPattern: route
                });
              }
            } catch (error) {
              console.error(`[RouteMappingService] Failed to create regex for route: ${route}`, error);
            }
          });
        }
      }

      this.initialized = true;
    } catch (error) {
      console.warn("Failed to fetch route mappings, using defaults", error);
      this.initialized = true;
    }
  }

  getUuid(route: string): string | undefined {
    // Find the first matching pattern
    for (const { pattern, info } of this.routes) {
      if (pattern.test(route)) {
        return info.uuid;
      }
    }
    return undefined;
  }

  extractUrlParams(url: string): { uuid: string; params: Record<string, string> } | null {
    // Extract path from absolute URL if needed
    let path = url;
    try {
      const urlObj = new URL(url);
      path = urlObj.pathname;
    } catch {
      // Not a valid URL, assume it's already a path
    }

    // Remove query parameters for matching
    const pathWithoutParams = path.split("?")[0];

    // Check all routes
    for (const { pattern, info } of this.routes) {
      const matches = pathWithoutParams.match(pattern);
      if (matches) {
        if (!info.isPattern || !info.params) {
          // Static route - no params to extract
          return { uuid: info.uuid, params: {} };
        } else {
          // Pattern route - extract params
          const params: Record<string, string> = {};
          // Params start at index 1 (index 0 is the full match)
          info.params.forEach((paramName, index) => {
            if (matches[index + 1]) {
              params[paramName] = matches[index + 1];
            }
          });
          return { uuid: info.uuid, params };
        }
      }
    }

    return null;
  }

  isObfuscatedRoute(url: string): boolean {
    // Extract path from absolute URL if needed
    let path = url;
    try {
      const urlObj = new URL(url);
      path = urlObj.pathname;
    } catch {
      // Not a valid URL, assume it's already a path
    }

    // Remove query parameters for matching
    const pathWithoutParams = path.split("?")[0];

    // Check if any pattern matches the URL
    for (const { pattern } of this.routes) {
      if (pattern.test(pathWithoutParams)) {
        return true;
      }
    }
    return false;
  }

  replaceWithUuid(url: string): string {
    // Check cache first
    const cached = this.urlCache.get(url);
    if (cached) {
      return cached;
    }

    // Find the first matching pattern
    const extracted = this.extractUrlParams(url);
    if (extracted) {
      const replaced = `${environment.classicApiUrl}/api/v2/o/${extracted.uuid}/`;
      this.urlCache.set(url, replaced);
      return replaced;
    }

    return url;
  }

  /**
   * Get the obfuscated URL pattern for a given original URL path
   * @param originalPath The original URL path (e.g., "/api/v2/auth/login/")
   * @returns The obfuscated URL pattern (e.g., "/api/v2/o/uuid-here/") or null if not found
   */
  getObfuscatedUrl(originalPath: string): string | null {
    // Remove any domain/protocol if present
    let path = originalPath;
    try {
      const urlObj = new URL(originalPath);
      path = urlObj.pathname;
    } catch {
      // Not a valid URL, assume it's already a path
    }

    // Find matching route
    const extracted = this.extractUrlParams(path);
    if (extracted) {
      return `/api/v2/o/${extracted.uuid}/`;
    }

    return null;
  }

  /**
   * Get all possible obfuscated URL patterns for a given regex pattern
   * This is useful for interceptors that need to match against obfuscated URLs
   * @param pathPattern A regex pattern for original URLs
   * @returns An array of obfuscated URL patterns that match
   */
  getObfuscatedUrlsForPattern(pathPattern: RegExp): string[] {
    const obfuscatedUrls: string[] = [];

    for (const { pattern, info, originalPattern } of this.routes) {
      // Test if the stored pattern would match the given pattern
      // We need to be careful here as we're comparing regex patterns
      if (pathPattern.test(originalPattern.replace(/\\/g, "").replace(/[\^$]/g, ""))) {
        obfuscatedUrls.push(`/api/v2/o/${info.uuid}/`);
      }
    }

    return obfuscatedUrls;
  }
}
